import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { applyBranding } from "@/hooks/useBranding";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { PublicPage, BrandMark, StatusCard } from "@/components/public/PublicShell";
import { MfaStep } from "@/components/security/MfaStep";
import { motion } from "framer-motion";
import { Briefcase, UserRound, Loader2, ArrowRight, UploadCloud, ShieldCheck, Linkedin, CheckCircle2, Check, PauseCircle, MailCheck } from "lucide-react";

const DEMO = [
  { role: "Recruiter", email: "recruiter@hirestream.ai", password: "recruiter123" },
  { role: "Candidate", email: "candidate@hirestream.ai", password: "candidate123" },
  { role: "Admin", email: "ktkdaskrishna@gmail.com", password: "admin123" },
];

const redirectFor = (role) => (role === "super_admin" ? "/superadmin" : role === "admin" ? "/admin" : role === "recruiter" ? "/recruiter" : "/candidate");

// Role-adaptive brand content for the left panel.
const PERSONA = {
  candidate: {
    image: "/omani_candidate.jpg",
    eyebrow: "For candidates",
    caption: "Applied in one tap. Profile built by AI.",
    headline: "Get hired for who you really are.",
    blurb: "Upload your CV once. AI builds your profile and matches you to every role that fits.",
  },
  employer: {
    image: "/omani_employer.jpg",
    eyebrow: "For employers",
    caption: "Shortlist ready in days, not weeks",
    headline: "Hire with evidence, not gut feel.",
    points: ["AI drafts the role and the assessment", "Proctored, explainable assessments", "Ranked shortlists with the evidence behind every score"],
  },
};

const STRENGTH = ["Use at least 8 characters", "Weak", "Fair", "Good", "Strong"];
const strengthOf = (p) => {
  if (!p || p.length < 8) return 0;
  return 1 + [/[a-z]/.test(p) && /[A-Z]/.test(p), /\d/.test(p), /[^A-Za-z0-9]/.test(p)].filter(Boolean).length;
};

const GoogleGlyph = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
  </svg>
);

function BrandPanel({ pdata, heroSrc, isEmployer }) {
  return (
    <aside className="relative hidden lg:flex flex-col justify-between gap-8 overflow-hidden border-r border-border/70 bg-[#0B1012] p-12 short:p-10">
      <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "linear-gradient(#2FD4A0 1px,transparent 1px),linear-gradient(90deg,#2FD4A0 1px,transparent 1px)", backgroundSize: "48px 48px" }} aria-hidden />
      <div className="pointer-events-none absolute -top-40 -left-24 h-[30rem] w-[30rem] rounded-full bg-primary/15 blur-[130px]" aria-hidden />
      <div className="relative"><BrandMark testId="login-brand" /></div>
      <div className="relative space-y-6 short:space-y-4 max-w-[520px]">
        <p className={cn("text-sm font-semibold uppercase tracking-[0.12em]", isEmployer ? "text-primary" : "text-sky-300")}>{pdata.eyebrow}</p>
        <h1 className="font-display text-4xl xl:text-5xl font-bold tracking-tight leading-[1.05]" data-testid="brand-headline">{pdata.headline}</h1>
        <div className="relative overflow-hidden rounded-2xl border border-border shadow-2xl shadow-black/40">
          <img key={heroSrc} src={heroSrc} alt="" decoding="async" className="h-64 xl:h-72 short:h-48 w-full object-cover" data-testid="brand-image" />
          <div className="absolute inset-x-3 bottom-3 flex items-center gap-2 rounded-lg bg-black/60 backdrop-blur-md px-3 py-2 text-sm text-white">
            <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />{pdata.caption}
          </div>
        </div>
        {pdata.points
          ? <ul className="space-y-2.5">{pdata.points.map((t) => <li key={t} className="flex items-center gap-2.5 text-[15px] text-foreground/85"><Check className="h-4 w-4 text-primary shrink-0" />{t}</li>)}</ul>
          : <p className="text-base leading-7 text-muted-foreground">{pdata.blurb}</p>}
      </div>
      <p className="relative text-xs text-muted-foreground">© {new Date().getFullYear()} HireStream</p>
    </aside>
  );
}

function Overlay({ testId, children }) {
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm p-6" data-testid={testId}>{children}</div>;
}

export default function Login() {
  const { login, register, resendVerification } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const roleParam = params.get("role");

  // Single source of truth for which persona is signing in / up.
  const [persona, setPersona] = useState(roleParam === "recruiter" ? "employer" : "candidate");
  const isEmployer = persona === "employer";
  const backendRole = isEmployer ? "recruiter" : "candidate";
  const pdata = PERSONA[persona];
  const [hero, setHero] = useState({});
  useEffect(() => { api.get("/public/hero-images").then(({ data }) => setHero(data || {})).catch(() => {}); }, []);
  const heroSrc = (isEmployer ? hero.login_employer : hero.login_candidate) || pdata.image;

  const [tab, setTab] = useState(roleParam || params.get("mode") === "signup" ? "signup" : "signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [verifySent, setVerifySent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [providers, setProviders] = useState({ google: true, linkedin: false });
  const [showDemo, setShowDemo] = useState(false);
  const [paused, setPaused] = useState(false);
  const [mfa, setMfa] = useState(null);
  useEffect(() => { if (params.get("reason") === "idle") toast.message("You were signed out after 30 minutes without activity."); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const signup = tab === "signup";
  const strength = strengthOf(password);

  useEffect(() => { applyBranding({ brand_primary: "#10B981" }); }, []);
  useEffect(() => { api.get("/auth/providers").then(({ data }) => setProviders(data)).catch(() => {}); }, []);

  const afterAuth = (user, viaCV) => {
    if (user.role === "candidate") nav(viaCV ? "/candidate/onboarding" : "/candidate");
    else nav(redirectFor(user.role));
  };

  const submit = async (e, viaCV = false) => {
    e?.preventDefault();
    if (signup) {
      if ((password || "").length < 8) { toast.error("Password must be at least 8 characters"); return; }
      if (password !== confirm) { toast.error("Passwords don't match"); return; }
    }
    setBusy(true);
    let res;
    if (signup) res = await register({ name, email, password, role: backendRole });
    else res = await login(email, password);
    setBusy(false);
    if (res.ok && res.verificationRequired) {
      if (viaCV) localStorage.setItem("hs_after_verify", "/candidate/onboarding");
      setVerifySent(res.email || email);
      return;
    }
    if (res.ok) {
      toast.success(signup ? `Welcome, ${res.user.name.split(" ")[0]}` : `Welcome back, ${res.user.name.split(" ")[0]}`);
      if (res.mfaSuggest) toast.message("Protect this account: turn on 2-step sign-in in Account.", { duration: 8000 });
      afterAuth(res.user, viaCV);
    } else if (res.mfa) { setMfa(res.mfa); }
    else if (res.needsVerification) {
      setVerifySent(res.email || email);
    } else if (/paused/i.test(res.error || "")) { setPaused(true); }
    else toast.error(res.error);
  };

  const resend = async () => {
    setBusy(true);
    await resendVerification(verifySent);
    setBusy(false);
    toast.success("Verification link sent. Check your inbox and spam folder.");
  };

  const quick = async (d) => {
    setBusy(true);
    const res = await login(d.email, d.password);
    setBusy(false);
    if (res.ok) { toast.success(`Signed in as ${d.role}`); nav(redirectFor(res.user.role)); }
    else if (res.mfa) { setMfa(res.mfa); }
    else if (/paused/i.test(res.error || "")) { setPaused(true); }
    else toast.error(res.error);
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleSignIn = () => {
    // Carry the chosen persona through the OAuth round-trip so a brand-new
    // social account is created with the RIGHT role (never defaults to admin).
    sessionStorage.setItem("hs_intended_role", backendRole);
    const redirectUrl = window.location.origin + "/auth/social";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const linkedinSignIn = () => {
    sessionStorage.setItem("hs_intended_role", backendRole);
    window.location.href = `${API}/auth/linkedin/login`;
  };

  const PersonaTab = ({ id, icon: Icon, label }) => (
    <button type="button" onClick={() => setPersona(id)} data-active={persona === id} data-testid={`persona-${id}`} aria-pressed={persona === id}
      className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold rounded-lg transition-colors data-[active=true]:bg-primary data-[active=true]:text-primary-foreground data-[active=false]:text-muted-foreground data-[active=false]:hover:text-foreground">
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
  const heading = signup ? (isEmployer ? "Create your employer account" : "Create your candidate account") : "Welcome back";
  const sub = signup
    ? (isEmployer ? "Your hiring workspace is set up as soon as you sign up." : "Sign up, upload your CV, and AI builds your profile.")
    : (isEmployer ? "Sign in to your hiring workspace." : "Sign in to see your matches and applications.");
  const social = "flex items-center justify-center gap-2.5 h-11 rounded-xl border border-border bg-card text-sm font-semibold hover:border-primary/50 transition-colors";

  return (
    <PublicPage className="grid lg:grid-cols-2">
      {mfa && <MfaStep challenge={mfa} onCancel={() => setMfa(null)} onDone={(u) => { setMfa(null); toast.success(`Welcome back, ${u.name.split(" ")[0]}`); afterAuth(u); }} />}
      {paused && (
        <Overlay testId="workspace-paused-screen">
          <StatusCard icon={PauseCircle} tone="amber" title="Your workspace is paused"
            actions={<Button variant="outline" onClick={() => setPaused(false)} data-testid="paused-back-btn" className="w-full">Back to sign in</Button>}>
            This organization's HireStream workspace has been paused. Please contact your organization's admin to restore access. Your data is safe and nothing has been deleted.
          </StatusCard>
        </Overlay>
      )}
      {verifySent && (
        <Overlay testId="verify-email-screen">
          <StatusCard icon={MailCheck} tone="primary" title="Check your inbox"
            actions={<>
              <div className="grid grid-cols-2 gap-2.5 w-full">
                <Button variant="outline" asChild><a href="https://mail.google.com" target="_blank" rel="noopener noreferrer" data-testid="verify-open-gmail">Open Gmail</a></Button>
                <Button variant="outline" asChild><a href="https://outlook.live.com/mail" target="_blank" rel="noopener noreferrer" data-testid="verify-open-outlook">Open Outlook</a></Button>
              </div>
              <Button onClick={resend} disabled={busy} data-testid="verify-resend-btn" className="w-full">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Resend verification link"}</Button>
              <button onClick={() => { setVerifySent(null); setTab("signin"); }} data-testid="verify-back-btn" className="text-sm text-primary hover:underline">Use a different email or sign in</button>
            </>}>
            We sent a verification link to <strong className="text-foreground" data-testid="verify-email-address">{verifySent}</strong>. Click it to activate your account and you'll be signed in automatically. It expires in 24 hours.
          </StatusCard>
        </Overlay>
      )}

      <BrandPanel pdata={pdata} heroSrc={heroSrc} isEmployer={isEmployer} />

      <main className="flex flex-col min-h-dvh">
        <div className="lg:hidden px-5 pt-5"><BrandMark testId="login-brand-mobile" /></div>
        <div className="flex-1 flex items-center justify-center px-5 py-10 sm:px-10">
          <motion.div key={`${persona}-${tab}`} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", stiffness: 260, damping: 26 }} className="w-full max-w-[420px] space-y-5">
            <div className="flex gap-1 p-1 rounded-xl bg-card border border-border" data-testid="persona-selector" aria-label="I'm signing in as">
              <PersonaTab id="candidate" icon={UserRound} label="Candidate" />
              <PersonaTab id="employer" icon={Briefcase} label="Employer" />
            </div>

            <div className="space-y-1">
              <h2 className="font-display text-[28px] font-semibold tracking-tight" data-testid="login-heading">{heading}</h2>
              <p className="text-sm text-muted-foreground">{sub}</p>
            </div>

            <div className={cn("grid gap-2.5", providers.linkedin && "grid-cols-2")}>
              <button onClick={googleSignIn} data-testid="google-signin" className={social}><GoogleGlyph />{providers.linkedin ? "Google" : `Continue with Google${isEmployer ? " Workspace" : ""}`}</button>
              {providers.linkedin && <button onClick={linkedinSignIn} data-testid="linkedin-signin" className={social}><Linkedin className="h-4 w-4 text-[#0A66C2]" />LinkedIn</button>}
            </div>

            <div className="flex items-center gap-3 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" />or with email<span className="h-px flex-1 bg-border" /></div>

            {!isEmployer && signup && (
              <button onClick={(e) => submit(e, true)} disabled={busy || !name || !email || !password} data-testid="upload-cv-cta"
                className="w-full rounded-xl border border-primary/40 bg-primary/10 p-4 text-left hover:bg-primary/15 transition-colors disabled:opacity-50 flex items-center gap-3">
                <span className="h-10 w-10 rounded-lg bg-primary/20 flex items-center justify-center shrink-0"><UploadCloud className="h-5 w-5 text-primary" /></span>
                <span><span className="block text-sm font-semibold">Sign up & upload CV</span><span className="block text-xs text-muted-foreground">Fastest path. AI builds your profile right away.</span></span>
              </button>
            )}

            <form onSubmit={submit} className="space-y-4">
              {signup && (
                <div className="space-y-2">
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" autoComplete="name" className="h-11" data-testid="signup-name" required />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">{isEmployer && signup ? "Work email" : "Email"}</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={isEmployer ? "you@company.com" : "you@email.com"} autoComplete="email" className="h-11" data-testid="login-email" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete={signup ? "new-password" : "current-password"} className="h-11" data-testid="login-password" required minLength={signup ? 8 : undefined} />
                {signup && password && (
                  <div className="space-y-1.5" data-testid="password-strength" data-score={strength}>
                    <div className="grid grid-cols-4 gap-1.5" aria-hidden>{[1, 2, 3, 4].map((n) => <span key={n} className={cn("h-1 rounded-full", n <= strength ? "bg-primary" : "bg-foreground/10")} />)}</div>
                    <p className="text-xs text-muted-foreground">{STRENGTH[strength]}{strength > 0 && strength < 4 ? ". Add a capital, number or symbol to make it stronger" : ""}</p>
                  </div>
                )}
              </div>
              {signup && (
                <div className="space-y-2">
                  <Label htmlFor="confirm">Confirm password</Label>
                  <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" autoComplete="new-password" className="h-11" data-testid="signup-confirm-password" required minLength={8} />
                </div>
              )}
              <Button type="submit" size="lg" className="w-full h-11 font-semibold" disabled={busy} data-testid="login-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{signup ? `Create ${isEmployer ? "employer" : "candidate"} account` : "Sign in"} <ArrowRight className="h-4 w-4 ml-1" /></>}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground">
              {signup
                ? <>Already have an account? <button type="button" onClick={() => setTab("signin")} data-testid="tab-signin" className="font-semibold text-primary hover:underline">Sign in</button></>
                : <>New here? <button type="button" onClick={() => setTab("signup")} data-testid="tab-signup" className="font-semibold text-primary hover:underline">{isEmployer ? "Create an employer account" : "Create a candidate account"}</button></>}
            </p>

            <div className="border-t border-border pt-5 space-y-3">
              <Button variant="outline" onClick={() => nav("/careers")} className="w-full h-11 gap-1.5" data-testid="view-careers-link">Just browsing? View open roles <ArrowRight className="h-4 w-4" /></Button>
              <div className="text-center">
                <button onClick={() => setShowDemo((s) => !s)} className="text-xs text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1.5" data-testid="toggle-demo">
                  <ShieldCheck className="h-3.5 w-3.5" /> {showDemo ? "Hide demo accounts" : "Explore with a demo account"}
                </button>
                {showDemo && (
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    {DEMO.map((d) => (
                      <button key={d.role} onClick={() => quick(d)} disabled={busy} data-testid={`demo-login-${d.role.toLowerCase()}`}
                        className="rounded-lg border border-border bg-card p-2.5 text-center text-xs font-medium hover:border-primary/50 transition-colors">{d.role}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </PublicPage>
  );
}
